import React, { useState, useEffect } from 'react';
import supabase from '../app/javascript/config/supabase';
import { RecurrenceEngine } from './recurrenceEngine.js';

const AutoScheduleCreator = ({ onSave, onCancel, editingSchedule = null }) => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [userTimezone, setUserTimezone] = useState('America/Los_Angeles');

  // Schedule form state
  const [schedule, setSchedule] = useState({
    name: '',
    description: '',
    template_id: '',
    recurrence_type: 'weekly',
    recurrence_pattern: {
      frequency: 'weekly',
      interval: 1,
      days: ['monday']
    },
    schedule_time: '3:00 PM',
    show_days_before: 1,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',

    blackout_dates: [],
    use_restaurant_periods: false,
    is_active: true
  });

  // Load data on component mount
  useEffect(() => {
    loadCurrentUser();
    loadTemplates();
    
    // If editing, populate form with existing schedule data
    if (editingSchedule) {
      setSchedule({
        ...editingSchedule,
        start_date: editingSchedule.start_date || new Date().toISOString().split('T')[0],
        end_date: editingSchedule.end_date || '',

        blackout_dates: editingSchedule.blackout_dates || []
      });
    }
  }, [editingSchedule]);

  const loadCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);
      
      // Try to detect user's timezone
      if (user && Intl.DateTimeFormat) {
        const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setUserTimezone(detectedTimezone);
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: storeData } = await supabase
        .from('store_users')
        .select('store_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!storeData) return;

      const { data: templatesData, error } = await supabase
        .from('count_templates')
        .select('id, name, description')
        .eq('store_id', storeData.store_id)
        .eq('is_archived', false)
        .eq('is_deleted', false)
        .order('name');

      if (!error && templatesData) {
        setTemplates(templatesData);
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleRecurrenceTypeChange = (type) => {
    let newPattern = { frequency: type, interval: 1 };
    
    switch (type) {
      case 'daily':
        newPattern = { frequency: 'daily', interval: 1 };
        break;
      case 'weekly':
        newPattern = { frequency: 'weekly', interval: 1, days: ['monday'] };
        break;
      case 'monthly':
        newPattern = { frequency: 'monthly', interval: 1, day_of_month: 1 };
        break;
      case 'custom':
        newPattern = { frequency: 'weekly', interval: 2, days: ['monday'] };
        break;
    }
    
    setSchedule(prev => ({
      ...prev,
      recurrence_type: type,
      recurrence_pattern: newPattern
    }));
  };

  const handlePatternChange = (key, value) => {
    setSchedule(prev => ({
      ...prev,
      recurrence_pattern: {
        ...prev.recurrence_pattern,
        [key]: value
      }
    }));
  };

  const handleWeekdayToggle = (day) => {
    const currentDays = schedule.recurrence_pattern.days || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day];
    
    handlePatternChange('days', newDays);
  };

  const handleSave = async () => {
    if (!schedule.name.trim()) {
      alert('Please enter a schedule name');
      return;
    }
    
    if (!schedule.template_id) {
      alert('Please select a template');
      return;
    }

    setLoading(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('You must be logged in to create schedules');
        return;
      }

      const { data: storeData } = await supabase
        .from('store_users')
        .select('store_id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .single();

      if (!storeData) {
        alert('No store found for your account');
        return;
      }

      const scheduleData = {
        store_id: storeData.store_id,
        created_by: user.id,
        name: schedule.name.trim(),
        description: schedule.description.trim(),
        template_id: schedule.template_id,
        recurrence_type: schedule.recurrence_type,
        recurrence_pattern: schedule.recurrence_pattern,
        schedule_time: schedule.schedule_time,
        user_timezone: userTimezone,
        show_days_before: schedule.show_days_before,
        start_date: schedule.start_date,
        end_date: schedule.end_date || null,

        blackout_dates: schedule.blackout_dates,
        use_restaurant_periods: schedule.use_restaurant_periods,
        is_active: schedule.is_active,
        next_generation_date: schedule.start_date
      };

      let result;
      if (editingSchedule) {
        // Update existing schedule
        result = await supabase
          .from('count_schedules')
          .update(scheduleData)
          .eq('id', editingSchedule.id)
          .select()
          .single();
      } else {
        // Create new schedule
        result = await supabase
          .from('count_schedules')
          .insert(scheduleData)
          .select()
          .single();
      }

      if (result.error) {
        console.error('Error saving schedule:', result.error);
        alert('Error saving schedule: ' + result.error.message);
        return;
      }

      console.log('Schedule saved successfully:', result.data);
      
      // Generate upcoming count sessions using the recurrence engine
      const recurrenceEngine = new RecurrenceEngine();
      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 3); // Generate 3 months ahead
      
      const upcomingSessions = recurrenceEngine.generateSessions(
        result.data,
        new Date(),
        endDate,
        50 // Max 50 sessions
      );
      
      console.log(`Generated ${upcomingSessions.length} upcoming sessions`);
      
      // Save the generated sessions to scheduled_count_sessions table
      if (upcomingSessions.length > 0) {
        const { error: sessionsError } = await supabase
          .from('scheduled_count_sessions')
          .insert(upcomingSessions);
          
        if (sessionsError) {
          console.error('Error saving scheduled sessions:', sessionsError);
          // Don't fail the whole operation, just log the error
        } else {
          console.log('Successfully saved scheduled sessions');
        }
      }
      
      onSave(result.data);
      
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Error saving schedule: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const weekdays = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="mb-0">
          {editingSchedule ? 'Edit Auto-Schedule' : 'Create Auto-Schedule'}
        </h5>
        <p className="text-muted mb-0">Set up recurring count sessions</p>
      </div>
      <div className="card-body">
        <form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
          {/* Basic Information */}
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Schedule Name *</label>
              <input
                type="text"
                className="form-control"
                value={schedule.name}
                onChange={(e) => setSchedule(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Weekly Liquor Count"
                required
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Template *</label>
              <select
                className="form-select"
                value={schedule.template_id}
                onChange={(e) => setSchedule(prev => ({ ...prev, template_id: e.target.value }))}
                required
              >
                <option value="">Select a template...</option>
                {templates.map(template => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <label className="form-label">Description</label>
              <textarea
                className="form-control"
                rows="2"
                value={schedule.description}
                onChange={(e) => setSchedule(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Optional description of this schedule"
              />
            </div>
          </div>

          {/* Recurrence Pattern */}
          <div className="mb-4">
            <h6>Recurrence Pattern</h6>
            <div className="row">
              <div className="col-md-4">
                <label className="form-label">Frequency</label>
                <select
                  className="form-select"
                  value={schedule.recurrence_type}
                  onChange={(e) => handleRecurrenceTypeChange(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              
              {schedule.recurrence_type !== 'daily' && (
                <div className="col-md-4">
                  <label className="form-label">Every</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      min="1"
                      max="52"
                      value={schedule.recurrence_pattern.interval}
                      onChange={(e) => handlePatternChange('interval', parseInt(e.target.value))}
                    />
                    <span className="input-group-text">
                      {schedule.recurrence_type === 'weekly' ? 'week(s)' : 
                       schedule.recurrence_type === 'monthly' ? 'month(s)' : 'period(s)'}
                    </span>
                  </div>
                </div>
              )}
              
              <div className="col-md-4">
                <label className="form-label">Time</label>
                <input
                  type="time"
                  className="form-control"
                  value={schedule.schedule_time.replace(/\s*(AM|PM)/, '')}
                  onChange={(e) => {
                    const time = e.target.value;
                    const [hours, minutes] = time.split(':');
                    const hour12 = parseInt(hours) > 12 ? parseInt(hours) - 12 : parseInt(hours);
                    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                    setSchedule(prev => ({ 
                      ...prev, 
                      schedule_time: `${hour12}:${minutes} ${ampm}` 
                    }));
                  }}
                />
              </div>
            </div>
          </div>

          {/* Weekly Pattern */}
          {(schedule.recurrence_type === 'weekly' || schedule.recurrence_type === 'custom') && (
            <div className="mb-4">
              <label className="form-label">Days of Week</label>
              <div className="d-flex gap-2 flex-wrap">
                {weekdays.map(day => (
                  <button
                    key={day.key}
                    type="button"
                    className={`btn btn-sm ${
                      schedule.recurrence_pattern.days?.includes(day.key) 
                        ? 'btn-primary' 
                        : 'btn-outline-secondary'
                    }`}
                    onClick={() => handleWeekdayToggle(day.key)}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Monthly Pattern */}
          {schedule.recurrence_type === 'monthly' && (
            <div className="mb-4">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Monthly Pattern</label>
                  <select
                    className="form-select"
                    value={schedule.recurrence_pattern.day_of_month ? 'date' : 'weekday'}
                    onChange={(e) => {
                      if (e.target.value === 'date') {
                        handlePatternChange('day_of_month', 1);
                        handlePatternChange('week_of_month', undefined);
                        handlePatternChange('day_of_week', undefined);
                      } else {
                        handlePatternChange('day_of_month', undefined);
                        handlePatternChange('week_of_month', 1);
                        handlePatternChange('day_of_week', 'monday');
                      }
                    }}
                  >
                    <option value="date">On specific date</option>
                    <option value="weekday">On specific weekday</option>
                  </select>
                </div>
                
                {schedule.recurrence_pattern.day_of_month !== undefined && (
                  <div className="col-md-6">
                    <label className="form-label">Day of Month</label>
                    <select
                      className="form-select"
                      value={schedule.recurrence_pattern.day_of_month}
                      onChange={(e) => handlePatternChange('day_of_month', parseInt(e.target.value))}
                    >
                      {Array.from({ length: 31 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>{i + 1}</option>
                      ))}
                      <option value={-1}>Last day of month</option>
                    </select>
                  </div>
                )}
                
                {schedule.recurrence_pattern.week_of_month !== undefined && (
                  <>
                    <div className="col-md-3">
                      <label className="form-label">Week</label>
                      <select
                        className="form-select"
                        value={schedule.recurrence_pattern.week_of_month}
                        onChange={(e) => handlePatternChange('week_of_month', parseInt(e.target.value))}
                      >
                        <option value={1}>1st</option>
                        <option value={2}>2nd</option>
                        <option value={3}>3rd</option>
                        <option value={4}>4th</option>
                        <option value={-1}>Last</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Day</label>
                      <select
                        className="form-select"
                        value={schedule.recurrence_pattern.day_of_week}
                        onChange={(e) => handlePatternChange('day_of_week', e.target.value)}
                      >
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Visibility and Timing */}
          <div className="mb-4">
            <h6>Visibility Settings</h6>
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Show in count interface</label>
                <select
                  className="form-select"
                  value={schedule.show_days_before}
                  onChange={(e) => setSchedule(prev => ({ ...prev, show_days_before: parseInt(e.target.value) }))}
                >
                  <option value={0}>Same day only</option>
                  <option value={1}>1 day before</option>
                  <option value={2}>2 days before</option>
                  <option value={3}>3 days before</option>
                  <option value={7}>1 week before</option>
                </select>
                <small className="text-muted">
                  Sessions always appear on calendar but only show in count interface based on this setting
                </small>
              </div>
            </div>
          </div>

          {/* Schedule Period */}
          <div className="mb-4">
            <h6>Schedule Period</h6>
            <div className="row">
              <div className="col-md-6">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={schedule.start_date}
                  onChange={(e) => setSchedule(prev => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">End Date (Optional)</label>
                <input
                  type="date"
                  className="form-control"
                  value={schedule.end_date}
                  onChange={(e) => setSchedule(prev => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Advanced Options */}
          <div className="mb-4">
            <h6>Advanced Options</h6>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="use_restaurant_periods"
                checked={schedule.use_restaurant_periods}
                onChange={(e) => setSchedule(prev => ({ ...prev, use_restaurant_periods: e.target.checked }))}
              />
              <label className="form-check-label" htmlFor="use_restaurant_periods">
                Use 4-4-5 restaurant accounting periods
              </label>
              <small className="text-muted d-block">
                Align schedules with standard restaurant accounting calendar
              </small>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingSchedule ? 'Update Schedule' : 'Create Schedule')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AutoScheduleCreator;
